import { useMemo } from "react";
import {
  ExtraWorkAggreementApprovalStatusResponse,
  GetExtraWorkAgreementQueryResponse,
  GetProjectResponse,
  ProjectRole,
} from "api/generatedApi";
import { useDashboardRestrictions } from "shared/user-restrictions/use-dashboard-restrictions";

// Mirrors ProjectExtraWorkAgreementValidatePolicy.isAllowedToEdit on the backend.
// Any change here must be kept in sync with the C# policy.

type NormalizedRole = "Owner" | "AssistantOwner" | "Manager" | "Participant" | "InactiveParticipant";

const normalizeEditorRole = (role?: ProjectRole): NormalizedRole | null => {
  switch (role) {
    case "ProjectOwner": return "Owner";
    case "ProjectAssistantOwner": return "AssistantOwner";
    case "ProjectManager": return "Manager";
    case "ProjectParticipant": return "Participant";
    case "ProjectInactiveParticipant": return "InactiveParticipant";
    default: return null;
  }
};

// Backend emits the ProjectAffiliateRole value-object value, e.g. "project owner".
const normalizeCreatorRole = (role?: string | null): NormalizedRole | null => {
  switch (role?.toLowerCase()) {
    case "project owner": return "Owner";
    case "project assistant owner": return "AssistantOwner";
    case "project manager": return "Manager";
    case "project participant": return "Participant";
    case "project inactive participant": return "InactiveParticipant";
    default: return null;
  }
};

const pairKey = (editor: NormalizedRole, creator: NormalizedRole) => `${editor}|${creator}`;

const DRAFT_EDIBILITY: ReadonlySet<string> = new Set([
  pairKey("Participant", "Owner"),
  pairKey("Participant", "AssistantOwner"),
  pairKey("Owner", "Owner"),
  pairKey("Owner", "AssistantOwner"),
  pairKey("Owner", "Participant"),
  pairKey("AssistantOwner", "Owner"),
  pairKey("AssistantOwner", "AssistantOwner"),
  pairKey("AssistantOwner", "Participant"),
  pairKey("Manager", "Manager"),
]);

const AWAITING_APPROVAL_EDIBILITY: ReadonlySet<string> = new Set([
  pairKey("Owner", "Manager"),
  pairKey("AssistantOwner", "Manager"),
  pairKey("Manager", "Owner"),
  pairKey("Manager", "AssistantOwner"),
  pairKey("Manager", "Participant"),
]);

const isAllowedToEdit = (
  status: ExtraWorkAggreementApprovalStatusResponse | undefined,
  editorRole: NormalizedRole | null,
  editorAffiliateId: string | undefined,
  creatorRole: NormalizedRole | null,
  creatorAffiliateId: string | undefined,
): boolean => {
  if (!status || !editorRole || !creatorRole) return false;
  switch (status) {
    case "UnderDevelopment":
      return (
        (!!editorAffiliateId && editorAffiliateId === creatorAffiliateId) ||
        DRAFT_EDIBILITY.has(pairKey(editorRole, creatorRole))
      );
    case "AwaitingApproval":
      return AWAITING_APPROVAL_EDIBILITY.has(pairKey(editorRole, creatorRole));
    default:
      return false;
  }
};

export interface ExtraWorkAgreementPermissions {
  canCreate: boolean;
  canSendToApproval: boolean;
  canEnterEditMode: boolean;
  // Non-status fields: editable only in edit mode and only while UnderDevelopment.
  canEditFields: (isEditing: boolean) => boolean;
  // Status field: editable in edit mode regardless of which editable status the agreement is in.
  canEditStatus: (isEditing: boolean) => boolean;
}

interface Args {
  project: GetProjectResponse;
  agreement?: GetExtraWorkAgreementQueryResponse;
}

export const useExtraWorkAgreementPermissions = ({
  project,
  agreement,
}: Args): ExtraWorkAgreementPermissions => {
  const restrictions = useDashboardRestrictions(project);

  const canCreate = restrictions.canCreateExtraWorkAgreements();
  const canSendToApproval = restrictions.canSendToApproval();

  return useMemo(() => {
    const status = agreement?.approvalStatus;
    const editorRole = normalizeEditorRole(project.currentUserRole);
    const creatorRole = normalizeCreatorRole(agreement?.creatorRole);

    const policyAllowsEdit = isAllowedToEdit(
      status,
      editorRole,
      project.currentUserAffiliateId,
      creatorRole,
      agreement?.creatorAffiliateId,
    );

    const canEnterEditMode = policyAllowsEdit;
    const isUnderDevelopment = status === "UnderDevelopment";
    // Participants never drive status transitions — the backend returns no
    // transitions for them, so rendering the Select would be visually empty.
    const roleCanEditStatus =
      editorRole === "Owner" || editorRole === "AssistantOwner" || editorRole === "Manager";

    return {
      canCreate,
      canSendToApproval,
      canEnterEditMode,
      canEditFields: (isEditing: boolean) => canEnterEditMode && isEditing && isUnderDevelopment,
      canEditStatus: (isEditing: boolean) => canEnterEditMode && isEditing && roleCanEditStatus,
    };
  }, [
    agreement?.approvalStatus,
    agreement?.creatorAffiliateId,
    agreement?.creatorRole,
    project.currentUserAffiliateId,
    project.currentUserRole,
    canCreate,
    canSendToApproval,
  ]);
};
